import { t } from "i18next";
import { Button, IconButton } from "@material-tailwind/react";
import {
  FunnelIcon
} from "@heroicons/react/24/outline";
import { ComponentType, useState } from "react";

import { useDrawerLayoutDispatch } from "../drawer-layout/DrawerLayout";

/**
* DrawerForm - drawer form container component
* @param data - form data
* @param FormComponent - form sub component
* @param onSubmit - submit callback
* @param closeDrawer - close drawer function
*/
function DrawerForm<T>({
  data,
  FormComponent,
  onSubmit,
  closeDrawer
}: {
  data: T,
  onSubmit: (data: T) => void,
  closeDrawer: () => void,
  FormComponent: ComponentType<{
    data?: T,
    onChange: (name: string, value: any) => void,
    onSubmit: () => void,
    submitted?: boolean
  }>
}) {
  const [dataState, setDataState] = useState<T>(data);
  const [submitted, setSubmitted] = useState<boolean>(false);

  /**
  * formChange - handle form change
  * @param name - object key name
  * @param value - object key value
  */
  const formChange = (name: string, value: any) => {
    setDataState((prev) => {
      const tmp = { ...prev as any };
      tmp[name] = value;
      return tmp;
    });
  };
  /***/

  /**
  * submitForm: trigger form submit event 
  */
  const submitForm = () => {
    setSubmitted(true);
    onSubmit(dataState);
  };
  /***/

  return (
    <div className="drawer-form">
      <div className="content">
        <FormComponent data={dataState}
          onChange={formChange}
          onSubmit={() => onSubmit(dataState)}
          submitted={submitted} />
      </div>


      <div className="footer">
        <div className="text-xs text-gray-400 italic">{t("common:mandatoryField")}</div>

        <Button size="sm" variant="outlined" onClick={closeDrawer}>
          {t("common:cancel")}
        </Button>

        <Button size="sm" color="blue" onClick={() => submitForm()}>
          {t("common:validate")}
        </Button>
      </div>
    </div>
  );
}
/***/

/**
* EditCreateDrawer: manage edit/create form
* create or edit entity via drawer form
* @param data - form data, editing purpose
* @param entityName - entity name for header display
* @param FormComponent - form sub component
* @param onSubmit - on submit event
* @param isNotCreatable - disable creation button
* @param isNotEditable - disable edition button
*/
export default function FilterDrawer<T>({
  data,
  entityName = "",
  FormComponent,
  onSubmit,
  size,
  noFlex,
  isNotCreatable,
  isNotEditable
}: {
  data?: T,
  entityName?: string,
  FormComponent: ComponentType<{
    data?: T,
    onChange: (name: string, value: any) => void,
    onSubmit: () => void,
    submitted?: boolean,
    customer?: EasyTech.Customer
  }>,
  onSubmit?: (value: T) => Promise<void>,
  isNotEditable?: boolean,
  isNotCreatable?: boolean,
  size?: "sm" | "md" | "lg",
  noFlex?: boolean
}) {
  const dispatch = useDrawerLayoutDispatch();

  /**
  * openCreateDrawer - Open drawer to customer create 
  */
  const openCreateDrawer = () => {
    dispatch({
      type: "open",
      title: t("common:filter") + " " + entityName,
      content: <DrawerForm data={data || ({} as T)}
        onSubmit={submitEvent}
        closeDrawer={closeDrawer}
        FormComponent={FormComponent} />
    });
  };
  /***/

  /**
  * closeDrawer - close drawer 
  */
  const closeDrawer = () => {
    dispatch({ type: "close" });
  };
  /***/

  /**
  * submitEvent - trigger onSubmit callback
  * @param data - form data
  */
  const submitEvent = (data: T) => {
    if (onSubmit) {
      onSubmit(data)
        .then(() => closeDrawer())
        .catch((err) => console.error(err));
    }
  };
  /***/

  return (
    <div className={noFlex ? "" : "flex items-center"}>
      <IconButton onClick={openCreateDrawer}
        variant="text"
        className={"icon-btn bg-white text-blue-gray-900 shadow-md shadow-blue-gray-500/10 hover:shadow-lg hover:shadow-blue-gray-500/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none" + (isNotCreatable ? "hidden" : "")}
        size={size ? size : "sm"}>
        <FunnelIcon className="w-[24px] text-black" />
      </IconButton>
    </div>
  );
}
/***/
